// Presentation only: these helpers never decide whether access is allowed.
export function getGateAccessReason(matchedGates, logic, configs = new Map(), donations = null) {
    const reasons = (matchedGates || []).filter(gate => gate && gate.type !== 'public').map(gate => {
        const config = gate.listId ? configs.get(gate.listId) : null;
        const type = config?.gateType || gate.type;
        const name = String(config?.name || '').trim();
        switch (type) {
            case 'password': return 'your password unlock is valid';
            case 'scheduledAccess': return `${gate.scheduleName || 'the access window'} is open`;
            case 'token': return `you own a qualifying NFT${name ? ` from ${name}` : ''}`;
            case 'collection':
            case 'compressedCollection': return `you own an NFT from ${name || 'the required collection'}`;
            case 'tokensHeld': return `you hold at least ${gate.tokenThreshold} ${gate.currency || 'SOL'}`;
            case 'donation': {
                const campaign = donations?.get(String(gate.donationCompId || ''))?.campaignName || gate.campaignName;
                return `your donation${campaign ? ` to ${campaign}` : ''} meets the requirement`;
            }
            case 'directPayment': return 'your payment is confirmed';
            default: return 'the access requirements are met';
        }
    });
    if (logic === 'all' && reasons.length > 1) return `access because all requirements are met: ${reasons.join('; ')}`;
    return reasons.length ? `access because ${reasons[0]}` : 'access';
}

export function getPanelRevealTitle(comp = {}, fallback = '') {
    const first = comp.sections?.[0];
    const title = [comp.title, comp.alt, comp.heading, first?.heading, first?.title, comp.campaignName, fallback]
        .find(value => typeof value === 'string' && value.trim());
    const typeTitles = { image: 'this image', video: 'this video', audio: 'this audio', text: 'this content',
        html: 'this experience', socialLinks: 'Social Links', social: 'Social Links', mint: 'Mint NFT', ad: 'this sponsored content' };
    return title?.trim() || typeTitles[comp.type] || 'this content';
}

// CSS variables travel through the existing component style save path.
export const GATE_REVEAL_FONTS = [
    'system-ui', 'Inter, sans-serif', 'Roboto, sans-serif', 'Montserrat, sans-serif',
    'Georgia, serif', "'Times New Roman', serif", "'Courier New', monospace",
];
export const GATE_REVEAL_EFFECTS = ['confetti', 'sparks', 'glitch', 'random'];
const colorOrDefault = (value, fallback) => typeof value === 'string' && /^#(?:[0-9a-f]{6}|[0-9a-f]{8})$/i.test(value) ? value : fallback;

export function getGateRevealAppearance(style = {}) {
    return {
        panelColor: colorOrDefault(style['--gate-reveal-panel-color'], '#123338'),
        textColor: colorOrDefault(style['--gate-reveal-text-color'], '#ffffff'),
        fontFamily: GATE_REVEAL_FONTS.includes(style['--gate-reveal-font']) ? style['--gate-reveal-font'] : 'system-ui',
        effect: GATE_REVEAL_EFFECTS.includes(style['--gate-reveal-effect']) ? style['--gate-reveal-effect'] : 'confetti',
    };
}

// Resolve random once in the click handler so rerenders cannot change an active effect.
export function resolveGateRevealEffect(effect) {
    const choices = GATE_REVEAL_EFFECTS.filter(value => value !== 'random');
    return effect === 'random' ? choices[Math.floor(Math.random() * choices.length)]
        : choices.includes(effect) ? effect : 'confetti';
}

export function getGateHighlight(style = {}) {
    const mode = style['--gate-highlight-mode'];
    const seconds = parseFloat(style['--gate-highlight-duration']);
    return {
        enabled: style['--gate-highlight-enabled'] !== 'off',
        color: colorOrDefault(style['--gate-highlight-color'], '#7ed786'),
        mode: ['steady', 'gentle', 'breathe'].includes(mode) ? mode : 'gentle',
        duration: Number.isFinite(seconds) ? Math.max(2, Math.min(8, seconds)) : 3,
    };
}
